/**
 * The one place the browser talks to `media-api`.
 *
 * Two things here are load-bearing and easy to get wrong:
 *
 * 1. **It sends the ID token, never the access token.** The authorizer is
 *    configured against the ID token, and `email` is an ID-token claim.
 *
 *    This used to carry a sharper warning — the custom `displayName` and `team`
 *    claims were ID-token-only, so sending the access token attributed every
 *    photograph in the gallery to "Guest" while nothing 401'd. D55 moved both
 *    off the token entirely, so that particular trap is gone. The rule stays:
 *    the two tokens are not interchangeable, and this is the only place that
 *    decides which one goes.
 *
 * 2. **A 401 means the session is genuinely finished.** `currentIdToken`
 *    already refreshes ahead of expiry, so reaching the network without a
 *    usable token is not a retryable condition.
 */
import { AUTH } from './auth-config';
import { currentIdToken } from './tokens';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown when there is no usable token at all. The caller's job is to send
 *  the guest back to sign in, not to retry. */
export class NotSignedIn extends Error {
  constructor() {
    super('not signed in');
    this.name = 'NotSignedIn';
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await currentIdToken();
  if (!token) throw new NotSignedIn();
  return { Authorization: `Bearer ${token}` };
}

export async function api<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const headers: Record<string, string> = {
    ...(await authHeader()),
    ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(rest.headers as Record<string, string> | undefined),
  };

  const res = await fetch(`${AUTH.api}${path}`, {
    ...rest,
    headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (res.status === 401) throw new NotSignedIn();

  if (!res.ok) {
    // The API's error shape is `{ error, message }`, and `message` is written
    // to be safe to show a guest. A response that is not that shape is a
    // gateway or a proxy talking, so it gets a sentence of our own.
    const body = await res.json().catch(() => null) as { error?: string; message?: string } | null;
    throw new ApiError(
      res.status,
      body?.error ?? 'http_error',
      body?.message ?? 'Something went wrong at our end. Try that again in a moment.',
    );
  }

  if (res.status === 204) return undefined as T;
  return await res.json() as T;
}

/**
 * A presigned POST upload. The fields S3 hands back must be appended to the
 * form **before** the file — S3 reads the policy as it streams and rejects a
 * form where the file arrives first.
 */
export async function uploadPresigned(
  target: { url: string; fields: Record<string, string> },
  body: Blob,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const form = new FormData();
  // `target.fields` is complete — `createPresignedPost` already includes
  // Content-Type in it, because the presigner puts every field the policy
  // signs into the response. Appending it again put two values in the form and
  // S3 answered every upload with
  //   403 Policy Condition failed: ["eq", "$Content-Type", "image/webp"]
  // which reads like a permissions problem and is not one. Append the fields
  // and nothing else.
  for (const [k, v] of Object.entries(target.fields)) form.append(k, v);
  // `file` must come last: S3 reads the form as it streams and stops at the
  // file, so any field after it is never seen.
  form.append('file', body);
  await xhrSend('POST', target.url, form, onProgress, signal);
}

/** A presigned PUT — used for multipart parts, which are raw bytes rather than
 *  a form. Resolves with the part's ETag, which S3 returns in a header and
 *  `complete` cannot be assembled without. */
export async function uploadPart(
  url: string,
  body: Blob,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  const xhr = await xhrSend('PUT', url, body, onProgress, signal);
  const etag = xhr.getResponseHeader('ETag');
  if (!etag) {
    // The bucket must expose ETag through CORS or this is unreachable, and the
    // upload cannot be completed without it. Worth naming precisely.
    throw new Error('S3 did not return an ETag for this part (check the bucket CORS exposedHeaders)');
  }
  return etag;
}

/**
 * XHR rather than fetch, for one reason: upload progress. `fetch` still has no
 * way to observe how many bytes of a request body have gone out, and a queue
 * that cannot show progress is the thing that makes a guest upload everything
 * twice.
 */
function xhrSend(
  method: string,
  url: string,
  body: XMLHttpRequestBodyInit,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr);
      else reject(new Error(`${method} failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('The connection dropped mid-upload.'));
    xhr.onabort = () => reject(new DOMException('cancelled', 'AbortError'));

    if (signal) {
      if (signal.aborted) return xhr.abort();
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  });
}
